import type { NostrMetadata } from '@nostrify/nostrify';
import { Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/CopyButton';
import type { FollowedUser } from '@/lib/feed';

interface FollowedUserCardProps {
  user: FollowedUser;
  metadata?: NostrMetadata;
  profilesLoading: boolean;
  feedUrl: string;
  onRemove: (pubkey: string) => void;
}

/** One followed person: identity preview plus their individual feed URL. */
export function FollowedUserCard({ user, metadata, profilesLoading, feedUrl, onRemove }: FollowedUserCardProps) {
  const name = metadata?.display_name || metadata?.name;
  const picture = metadata?.picture;
  const shortNpub = `${user.npub.slice(0, 12)}…${user.npub.slice(-4)}`;

  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm space-y-3">
      <div className="flex items-center gap-3">
        {profilesLoading ? (
          <>
            <Skeleton className="size-10 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </>
        ) : (
          <>
            <Avatar className="size-10 border">
              <AvatarImage src={picture} alt={name ?? shortNpub} />
              <AvatarFallback className="bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                {(name ?? user.npub.slice(5, 7)).slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{name ?? 'Unknown profile'}</p>
              <p className="text-xs text-muted-foreground font-mono truncate">{shortNpub}</p>
            </div>
          </>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(user.pubkey)}
          aria-label={`Remove ${name ?? shortNpub}`}
          className="text-muted-foreground hover:text-destructive shrink-0"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <code
          className="flex-1 min-w-0 truncate rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground"
          title={feedUrl}
        >
          {feedUrl}
        </code>
        <CopyButton text={feedUrl} label={`Copy feed URL for ${name ?? shortNpub}`} />
      </div>
    </div>
  );
}
